import {
  PropertyFragmentFragment,
  PropertyValueFragmentFragment,
} from "src/graphql/anti-fraud/__generated__/graphql";
import {
  mapBusinessTypeToAppDataType,
  mapDataTypeToAppDataType,
  parseTypedValue,
} from "src/mappers/typedValue.mappers";
import { Constraint, Property, PropertyCode, PropertyValue } from "src/types/property";

export const mapToConstraint = (
  constraint: PropertyValueFragmentFragment["property"]["constraints"][number]
): Constraint => ({
  code: constraint.code,
  dataType: constraint.dataType ? mapDataTypeToAppDataType(constraint.dataType) : "string",
  description: constraint.description,
  message: constraint.message,
  value: constraint.value,
});

export const mapToProperty = (fragment: PropertyFragmentFragment): Property => ({
  code: fragment.code,
  name: fragment.name,
  dataType: mapBusinessTypeToAppDataType(fragment.businessType),
  mandatory: fragment.mandatory,
  constraints: fragment.constraints.map(mapToConstraint),
  confListCode: fragment.confListCode,
});

export const mapToPropertyValue = (fragment: PropertyValueFragmentFragment): PropertyValue => ({
  value: parseTypedValue(
    fragment.value,
    mapBusinessTypeToAppDataType(fragment.property.businessType)
  ),
  property: mapToProperty(fragment.property),
});
// Liste des codes de propriété valides pour la vérification à l'exécution
const VALID_PROPERTY_CODES: readonly PropertyCode[] = [
  "id",
  "ville",
  "forme_juridique",
  "pays",
  "id_ret",
  "date",
  "etape",
  "sous_etape",
  "motif_refus",
  "region",
  "date_octroi",
  "date_signature",
  "montant_demande",
  "date_decaissement",
  "montant_decaisse",
  "montant_regle_total",
  "type_pret",
  "SIREN",
  "raison_sociale",
  "adresse_siege",
  "numera",
  "c_tiers",
  "APE",
  "CODE_APE",
  "ca",
  "resultat_net",
  "dette",
  "capital_social",
  "ratio",
  "IBAN_decaissement",
  "type_banque",
  "nom_banque",
  "date_creation",
  "pef_is_same_bpi_address",
  "pef_is_same_bpi_address_description",
  "nom",
  "prenom",
  "email",
  "test_id",
  "email_formulaire",
  "telephone",
  "telephone_formulaire",
  "nombre_echeances_impaye",
  "impayes_30jours",
  "impayes_90jours",
  "nombre_passages_impaye",
  "montant_impaye_capital",
  "montant_impaye_interets",
  "montant_impaye_accessoires",
  "montant_impaye_interets_retard",
  "date_passage_en_perte",
  "montant_passe_en_pertes",
  "pfe_siren",
  "pfe_date_creation",
  "pfe_nom_entreprise",
  "pfe_code_naf",
  "pfe_libelle_code_naf",
  "pfe_date_premiere_immatriculation_rcs",
  "pfe_procedure_collective_existe",
  "pfe_procedure_collective_en_cours",
  "pfe_entreprise_cessee",
  "pfe_date_cessation",
  "pfe_statut_rne",
  "pfe_date_radiation_rne",
  "pfe_status",
  "pfe_error_status",
  "pfe_execution_date",
  "application",
  "depense_carburant",
  "previsionnel_carburant",
  "forme_juridique",
  "forme_juridique_libelle",
  "capitaux_propres",
  "resultat_exploitation",
  "ratio_dettes_capital",
  "ratio_dettes_capitaux_propres",
  "date_immatriculation",
  "same_addresses",
  "same_addresses_description",
  "adresse",
  "ip",
  "date_naissance",
  "ville_naissance",
  "pays_naissance",
  "onFncRfList",
  "alertId",
  "createdAt",
  "createdBy",
  "alertQualification",
  "reportsCount",
  "reportsQualified",
  "reportsSightings",
  "reportsFalsePositives",
  "fraudCategory",
  "transactionType",
  "fraudSource",
  "transactionChannel",
  "accountClosed",
  "reports",
  "ibanSeen",
  "ibanMinAge",
  "whitelistedParty",
  "status",
  "errorStatus",
  "executionDate",
  "pays_immatriculation",
  "profession",
  "adresse_domicile",
  "adresse_pro",
  "ca_liasse_fiscale",
  "ca_declare",
  "revenu_annuel",
  "patrimoine_global",
  "origine_patrimoine",
  "origine_fonds",
  "type_banque_decaissement",
  "nom_banque_decaissement",
  "IBAN_encaissement",
  "type_banque_encaissement",
  "nom_banque_encaissement",
] as const;

/**
 * Type guard pour vérifier si une chaîne est un PropertyCode valide
 */
export const isPropertyCode = (code: string): code is PropertyCode => {
  return VALID_PROPERTY_CODES.includes(code as PropertyCode);
};

export const mapToPropertyCode = (code: string): PropertyCode | undefined => {
  if (!isPropertyCode(code)) {
    console.error("Add new property code", code);
  }
  if (isPropertyCode(code)) {
    return code;
  }
};
